<template>
  <PageHeader :title="$t('title')"  v-if="customer" back :save="isOnline" @back="toCustomer" @save="save" :valid='formValid' >
    <v-container fluid class="pa-0" >
      <v-row v-if="!isOnline">
        <v-col cols="12">
          <v-alert dense type="error" >
            You are offline. Because of the discount calculation, this transaction can not be handled  offline mode.
          </v-alert>
        </v-col>
      </v-row>

      <v-row v-if="!eligible">
        <v-col cols="12">
          <v-alert dense type="warning" >
            This customer is not eligible for a discount.
          </v-alert>
        </v-col>
      </v-row>

      <v-row>

        <v-col cols="12" class="d-flex justify-space-between">
          <div style="width:80%;">
            <v-select
              :items="categories"
              :label="$t('Category')"
              v-model="category"
              solo
              v-if="categories.length>2"
            ></v-select>
          </div>
          <div style="width:20%; text-align: right;" class="font-weight-bold" >
            {{$t('total')}}
            <Currency :value="totalCost" />
          </div>
        </v-col>

        <v-col cols="12">

          <v-data-table dense :headers="headers" :items="table" item-key="item.uuid" :items-per-page="-1" class="w-100" hide-default-footer :search="searchTrigger" :custom-filter="tableFilter" >

            <template v-slot:header.description>{{$t('Description')}}</template>
            <template v-slot:header.price_per_unit>{{$t('Price')}} <small>/ {{$t('unit')}}</small></template>
            <template v-slot:header.quantity>{{$t('Quantity')}}</template>
            <template v-slot:header.cost>{{$t('Cost')}}</template>

            <template v-slot:item.price_per_unit="{ item }">
              <Currency :value="item.price_per_unit" />
              <span class="hidden-xs-only pl-0"><small>/ {{ item.unit }}</small></span>
            </template>

            <template v-slot:item.quantity="{ item }">
              <v-text-field min=0 type='number' :rules="numberRules" style="text-align: right;" v-model="item.quantity" @input="updateCost(item.uuid)" />
            </template>

            <template v-slot:item.cost="{ item }" class="hidden-xs-only">
              <Currency :value="parseInt(item.quantity)*item.price_per_unit" />
            </template>


          </v-data-table>
        </v-col>

        <v-col cols="12 d-flex justify-space-between" class="font-weight-bold">
          <div></div>
          <div>
            {{$t('total')}}
            <Currency :value="totalCost" />
          </div>
        </v-col>

      </v-row>

      <v-row>
        <v-col cols="8" class="text-start">
          {{$t('total')}}
        </v-col>
        <v-col cols="4" class="text-end">
          <Currency :value="cost" />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="8" class="text-start">
          {{$t('priorUnits')}}
        </v-col>
        <v-col cols="4" class="text-end">
          <ServerSideResult :test="priorUnits">
            <NumberFormatter :value="priorUnits" unit="units" />
          </ServerSideResult>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="8" class="text-start">
          {{$t('discount')}}
        </v-col>
        <v-col cols="4" class="text-end">
          <Currency :value="discount" />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="8" class="text-start">
          {{$t('due')}}
        </v-col>
        <v-col cols="4" class="text-end">
          <Currency :value="totalCost" />
        </v-col>
      </v-row>

    </v-container>


  </PageHeader>
</template>


<script>
  import { v4 as uuidv4 } from 'uuid';
  import lookups from '@/mixins/lookups';
  import validationRules from '@/mixins/validationRules';
  import PageHeader from '@/components/PageHeader';
  import InputOrderEdit from '@/views/inputOrders/components/InputOrderEdit';
  import Currency from '@/components/Currency';
  import NumberFormatter from '@/components/NumberFormatter';
  import ServerSideResult from '@/components/ServerSideResult';
  

  export default {

    components: { PageHeader, InputOrderEdit, Currency, NumberFormatter, ServerSideResult },

    data: () => ({
      table: [],
      category: undefined,
      priorUnits: undefined,
    }),

    computed: {
      isOnline() { return this.$store.getters.networkOnline; },
      fieldAgentPayload() { return this.$store.getters.settings.field_agent_payload; },

      customerUuid() { return this.$route.query.uuid; },
      customer() { return this.$store.getters.customer(this.customerUuid); },
      skus() { return this.$store.getters.settings.skus; },
      currency() { return this.$store.getters.currency; },
      cost() { return this.table.map( e => (parseFloat(e.quantity) || 0)*parseFloat(e.price_per_unit) ).reduce((a, b) => a + b, 0); },

      fieldAgentUuid() { return this.$store.getters.settings.fieldAgentUuid; },

      eligible() {
        let e = true;
        e = e && (this.customer && this.customer.msisdn.length>8 && this.customer.demographic && (this.customer.name || '').length>2);
        e = e && (this.customerUuid!==this.fieldAgentUuid);
        if (this.fieldAgentPayload.tier) {
          e = e && (this.fieldAgentPayload.tier != 'Tier B')
        }
        return e;
      },

      discount() {
        let d = 0;
        if (this.eligible) {

          // every fifth element (1,6 etc) gets a 25% discount - and the discount must be calculated on the most expensive items
          // first item gets an extra 25%

          // what are we buying?
          let purchasing = [];
          this.table.forEach( e => {
            for (let i = 0; i < e.quantity; i++) {
              purchasing.push(e.price_per_unit);
            }
          });
          // sort this, cheapest to most expensive
          purchasing = purchasing.sort();

          // what have we bought (place holders)
          let units = [];
          for (let i = 0; i < this.priorUnits; i++) { units.push(0); }

          // build up our purchase history
          while (purchasing.length>0) {
            if ((units.length%5)===0) {
              units.push(purchasing.pop());
            } else {
              units.push(purchasing.shift());
            }
          }

          // calculate the discount
          if (units.length>0) {
            d = units[0]*0.25;
            units.forEach( (e,i) => { d = d + 0.25*e*((i%5)===0 ? 1 : 0) } );
          }
        
        }

        return d;
      },
      totalCost() { return (this.cost-this.discount); },
      formValid() { return (this.totalCost>0); },
      location() { return ((this.$store.getters.geolocation || {}).coords); },
      fieldAgentId() { return this.$store.getters.settings.fieldAgentId; },
      categories() { return ['All Categories'].concat([...new Set(this.skus.map(e => e.category))].filter(e => e).sort()); },

      headers() { return [
        {
          align: 'start',
          sortable: true,
          value: 'description',
        },
        {
          align: 'center',
          sortable: true,
          value: 'price_per_unit',
        },
        {
          align: 'start',
          sortable: false,
          value: 'quantity',
        },
        {
          align: 'end',
          sortable: false,
          value: 'cost',
        },
      ]; },

      search() { return this.$store.getters.search; },
      searchTrigger() { return (this.search || '') + (this.category || '') },
    },

    watch: {
      skus: {
        immediate: true,
        handler(to) {
          if (to) {
            this.table = to.map( e => {e.quantity = 0; e.cost = 0; e.uuid = uuidv4(); return e; } );
          }
        },
      },
      customer: {
        immediate: true,
        handler(to) {
          this.remoteLookup('urbanFarmerPriorUnits', { uuid: this.customer.uuid }).then( priorUnits => this.priorUnits = priorUnits ).catch(e => this.priorUnits = null);
        },
      },
    },

    methods: {
      updateCost(uuid) {
        let index = this.table.findIndex( e => e.uuid===uuid );
        let item = this.table[index];
        item.quantity = parseInt(item.quantity);
        item.cost = item.quantity*item.price_per_unit;
        this.$set(this.table, index, item);
      },
      tableFilter(value, searchTrigger, item) {
        let search = this.search || '';
        let category = (this.category || 'All Categories');
        let r = true;
        if (searchTrigger.length>0) {
          if (search.length>0) { r = r && item.description.match(new RegExp(this.search, 'i')); }
          if (category!=='All Categories') { r = r && item.category===category; }
        }
        return r;
      },
      toCustomer() { this.$router.push({ name: 'ShowCustomer', query: { uuid: this.customerUuid } }); },
      save() {

        const delivery_uuid = uuidv4();

        this.table.filter( e => (e.quantity || 0)>0 ).forEach( e => {

          const purchase = {
            uuid: e.uuid,

            delivery_uuid: delivery_uuid,
            delivery_discount: this.discount,

            createdAt: new Date().toISOString(),
            sku: e.code,
            description: e.description,
            price_per_unit: (parseFloat(e.price_per_unit) || 0),
            value: (parseFloat(e.price_per_unit) || 0) * (parseInt(e.quantity) || 0),
            quantity: (parseInt(e.quantity) || 0),
            status: 'placed',

            customerUuid: this.customerUuid,
            location: this.location,
            capturedById: this.fieldAgentId,
          }

          this.$store.dispatch('addPurchase', purchase);

        });

        this.toCustomer();
      },
    },

    mounted() {
      this.updateConnectionStatus(navigator.onLine);
    },

    mixins: [validationRules, lookups],

  }
</script>


<i18n>
{
  "en": {
    "title": "Purchase U-Mix",
    "Description": "Description",
    "Price": "Price",
    "unit": "Unit",
    "Quantity": "Quantity",
    "total": "Total",
    "priorUnits": "Units bought previously",
    "discount": "Discount",
    "due": "Balance Due",
    "Category": "Category"
  },
  "es": {
    "title": "Compra  U-Mix",
    "Description": "Descripción",
    "Price": "Precio",
    "unit": "Unidad",
    "Quantity": "Cantidad",
    "total": "Total",
    "priorUnits": "Unidades compradas anteriormente",
    "discount": "Descuento",
    "due": "Saldo Adeudado",
    "Category": "Category"
  },
  "sw": {
    "title": "Kununua U-Mix",
    "Description": "Maelezo",
    "Price": "Bei",
    "unit": "Kitengo",
    "Quantity": "Wingi",
    "total": "Jumla",
    "priorUnits": "Units zilizonunuliwa hapo awali",
    "discount": "Punguzo",
    "due": "Usawa Unastahili",
    "Category": "Jamii"
  }
}
</i18n>
